import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { subscribeFeedback, type FeedbackNotice } from "../lib/feedback";
import { IconButton } from "./IconButton";

/**
 * Pilha de avisos da V2, sobre o mesmo barramento `feedback.ts` do V1
 * (AGENTS §4.2): confirmação e erro nunca passam por diálogo nativo.
 *
 * Erro usa `role="alert"` e sucesso/informação usam `status`, para anunciar sem
 * roubar o foco (§17). O aviso some sozinho, mas sempre pode ser fechado.
 */
const AUTO_DISMISS_MS = 5200;

const ICONS = {
  success: CheckCircle2,
  error: AlertTriangle,
  info: Info,
} as const;

export function ToastHost() {
  const [notices, setNotices] = useState<FeedbackNotice[]>([]);

  useEffect(() => {
    const timers: number[] = [];
    const unsubscribe = subscribeFeedback((notice) => {
      setNotices((current) => [...current, notice]);
      timers.push(
        window.setTimeout(() => {
          setNotices((current) => current.filter((item) => item.id !== notice.id));
        }, AUTO_DISMISS_MS),
      );
    });
    return () => {
      unsubscribe();
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  if (notices.length === 0) return null;

  return (
    <div className="cx-toasts" aria-live="polite" aria-atomic="false">
      {notices.map((notice) => {
        const Icon = ICONS[notice.tone];
        return (
          <div
            key={notice.id}
            className="cx-toast"
            data-tone={notice.tone}
            role={notice.tone === "error" ? "alert" : "status"}
          >
            <Icon className="cx-toast-icon" size={16} aria-hidden="true" />
            <span style={{ flex: 1, minWidth: 0 }}>{notice.message}</span>
            <IconButton
              label="Fechar aviso"
              size="compact"
              variant="bare"
              icon={<X size={13} />}
              onClick={() =>
                setNotices((current) => current.filter((item) => item.id !== notice.id))
              }
            />
          </div>
        );
      })}
    </div>
  );
}
